/**
 * Menu navigation core (keyboard + gamepad + touch), shared by the web UI (apps/client `src/ui/nav.ts`: Preact hooks
 * and DOM focus) and the native shell (RN screens; gamepads ship in v1, so menus must route pad presses the same way).
 *
 * - ONE router subscribes to `InputManager.onMenu` and forwards each action to the TOP-MOST active layer only
 *   (overlays above screens), so a pause menu never also drives the HUD or the screen below.
 * - `inputStyle` tracks the last used device family so button hints show keys, pad buttons or nothing (touch).
 * - Arming windows (`createArming`) keep a screen that appears mid-play (Results, CONNECTION LOST) from being
 *   confirmed by the play keys the player is still mashing.
 * - `gridAction` is the focus-grid/list router (directions, edges, adjust, confirm/back/pause) every menu uses.
 *
 * Platform-neutral: signals-core, no DOM. The web adds DOM focus, hover-follows-focus and its keydown guards.
 */
import { batch, signal } from '@preact/signals-core';
import type { InputManager, MenuAction, MenuSource as InputMenuSource } from '../input/types';
import { ui } from './store';

/** Where a routed menu action came from. `deviceId` is the InputManager device ('kb:solo', 'pad:1', …). */
export interface MenuSource {
  kind: 'kb' | 'pad' | 'touch' | 'ui';
  deviceId: string | null;
}
export type MenuHandler = (action: MenuAction, source: MenuSource) => void;

export const UI_SOURCE: MenuSource = { kind: 'ui', deviceId: null };

/** Last device family used (button hints follow it). */
export const inputStyle = signal<'kb' | 'pad' | 'touch'>('kb');

interface Layer {
  id: number;
  priority: number;
  handler: { current: MenuHandler };
}

const layers: Layer[] = [];
let nextLayerId = 1;

/**
 * Hover-follows-focus only after a REAL pointer move: when a screen or overlay renders under a resting cursor the
 * browser fires mouseenter on whatever lands under it (e.g. the pause menu's FORFEIT row where the room's READY
 * button was), which must not steal the keyboard/pad focus from the default item. Cleared whenever a menu layer
 * registers; set by a pointer move whose position actually changed (`notePointerMove`).
 */
let pointerMoved = false;
let lastPointer: { x: number; y: number } | null = null;
/** Did the pointer move since the last menu layer appeared? */
export const hoverArmed = (): boolean => pointerMoved;
/** Record a pointer position; arms hover-focus when it differs from the previous one. */
export const notePointerMove = (x: number, y: number): void => {
  if (!lastPointer || lastPointer.x !== x || lastPointer.y !== y) pointerMoved = true;
  lastPointer = { x, y };
};

export const toMenuSource = (s: InputMenuSource | null | undefined): MenuSource => {
  if (!s) return UI_SOURCE;
  const kind =
    s.kind === 'gamepad' ? 'pad' : s.kind === 'touch' ? 'touch' : s.kind === 'keyboard' ? 'kb' : 'ui';
  return { kind, deviceId: s.deviceId };
};

/** Dispatch to the top-most layer (highest priority, most recent among equals). */
export const dispatchMenu = (action: MenuAction, source: MenuSource = UI_SOURCE): boolean => {
  let top: Layer | null = null;
  for (const l of layers) if (!top || l.priority >= top.priority) top = l;
  if (!top) return false;
  top.handler.current(action, source);
  return true;
};

/** Number of active layers (tests). */
export const activeLayerCount = (): number => layers.length;

/** Highest active priority (-Infinity when none). Lets a layer know whether something sits above it. */
export const topLayerPriority = (): number => {
  let p = -Infinity;
  for (const l of layers) if (l.priority > p) p = l.priority;
  return p;
};

/** Register a layer (the handler ref may change; the layer keeps reading `.current`). Returns its remover. */
export const registerMenuLayer = (handler: { current: MenuHandler }, priority = 0): (() => void) => {
  const layer: Layer = { id: nextLayerId++, priority, handler };
  layers.push(layer);
  pointerMoved = false;
  return () => {
    const i = layers.indexOf(layer);
    if (i >= 0) layers.splice(i, 1);
  };
};

export const LAYER = { SCREEN: 0, PANEL: 5, HUD: 8, OVERLAY: 10, MODAL: 20, CAPTURE: 100 } as const;

/**
 * Dispatch, and if an overlay layer CLOSED the in-match overlay with this very back / pause press, keep the overlay
 * up until the next task. The match scene listens to the same InputManager press after the router and opens PAUSE
 * on a keyboard Esc when no overlay is up, so a modal that closed itself synchronously (How To Play opened from the
 * Game Day card) used to land the player in the pause menu with one Esc (QA r3). Batched, so nothing re-renders.
 */
export const dispatchMenuConsumingClose = (action: MenuAction, source: MenuSource): void => {
  if (action !== 'back' && action !== 'pause') {
    dispatchMenu(action, source);
    return;
  }
  const before = ui.overlay.peek();
  if (before === 'none' || topLayerPriority() < LAYER.OVERLAY) {
    dispatchMenu(action, source);
    return;
  }
  let closed = false;
  batch(() => {
    dispatchMenu(action, source);
    if (ui.overlay.peek() === 'none') {
      closed = true;
      ui.overlay.value = before;
    }
  });
  if (!closed) return;
  setTimeout(() => {
    if (ui.overlay.peek() === before) ui.overlay.value = 'none';
  }, 0);
};

/**
 * Route the InputManager's menu actions to the layers (the platform-neutral half of the router; the web adds its
 * DOM keydown/focus guards around it). `swallow` may drop an action (web: a keyboard 'confirm' that already clicked a
 * tabbed-to button). Returns a disposer.
 */
export const routeMenuInput = (
  input: Pick<InputManager, 'onMenu'>,
  swallow: (action: MenuAction, source: MenuSource) => boolean = () => false,
): (() => void) =>
  input.onMenu((action, s) => {
    const source = toMenuSource(s);
    if (swallow(action, source)) return;
    if (source.kind !== 'ui') inputStyle.value = source.kind;
    dispatchMenuConsumingClose(action, source);
  });

// ── Arming (screens that appear mid-play) ─────────────────────────────────────────────────────────────────

/** Default arming delay of screens / modals that can appear while the player is mashing play keys. */
export const ARM_MS = 900;

/** Is a routed action blocked by an arming window? Only device presses of confirm / back / pause are. */
export const blockedWhileArming = (action: MenuAction, source: MenuSource): boolean =>
  source.kind !== 'ui' && (action === 'confirm' || action === 'back' || action === 'pause');

/** While arming, a blocked press keeps the window shut until the keys have been quiet this long (a mash). */
export const ARM_QUIET_MS = 700;

/**
 * Arming window state (the web's `useArming` hook and RN screens wrap it): `ready()` is false for `ms` after
 * `restart()` (mount, modal opening); a press blocked meanwhile (`poke()`) keeps it false until no press came for
 * ARM_QUIET_MS, so a player still mashing never confirms by accident. `readyAt()` is when it opens (clock ms).
 */
export interface Arming {
  ready(): boolean;
  poke(): void;
  restart(): void;
  readyAt(): number;
}

/** `ms`: the window (a getter when the caller's window can change between uses). */
export const createArming = (ms: number | (() => number), clock: () => number): Arming => {
  const win = typeof ms === 'function' ? ms : () => ms;
  let since = clock();
  let lastPoke = -Infinity;
  const readyAt = (): number => Math.max(since + win(), lastPoke + ARM_QUIET_MS);
  return {
    ready: () => win() <= 0 || clock() >= readyAt(),
    poke: () => {
      if (win() > 0) lastPoke = clock();
    },
    restart: () => {
      since = clock();
      lastPoke = -Infinity;
    },
    readyAt,
  };
};

// ── Focus lists / grids ───────────────────────────────────────────────────────────────────────────────────

/** Pure grid stepping: next index or null when blocked at an edge. */
export const stepIndex = (
  cur: number,
  delta: number,
  count: number,
  wrap: boolean,
  isDisabled?: (i: number) => boolean,
): number | null => {
  if (count <= 0) return null;
  let i = cur;
  for (let tries = 0; tries < count; tries++) {
    let n = i + delta;
    if (n < 0 || n >= count) {
      if (!wrap) return null;
      n = ((n % count) + count) % count;
    }
    i = n;
    if (!isDisabled?.(i)) return i;
  }
  return null;
};

/** What a focus grid / list does with menu actions (the web's `useFocusGrid` options minus rendering). */
export interface GridSpec {
  count: number;
  /** Columns (1 = vertical list). */
  cols?: number;
  /** Horizontal list (left/right move, up/down go to `onAdjust`). */
  horizontal?: boolean;
  wrap?: boolean;
  /** Skip disabled items while moving. */
  isDisabled?(i: number): boolean;
  onConfirm?(i: number, source: MenuSource): void;
  onBack?(source: MenuSource): void;
  onPause?(source: MenuSource): void;
  /** Left/right on a vertical list (sliders, toggles), up/down on a horizontal one. */
  onAdjust?(i: number, dir: -1 | 1, source: MenuSource): void;
  /** Movement past the edge when `wrap` is false (e.g. jump to another panel). */
  onEdge?(dir: 'up' | 'down' | 'left' | 'right', source: MenuSource): void;
}

/**
 * Route one menu action to a focus grid whose focused item is `cur`: confirm / back / pause / adjust / edge
 * callbacks, or a new index through `setIndex`. Arming is the caller's job (check `blockedWhileArming` first).
 */
export const gridAction = (
  o: GridSpec,
  cur: number,
  a: MenuAction,
  source: MenuSource,
  setIndex: (i: number) => void,
): void => {
  const cols = o.cols ?? 1;
  const wrap = o.wrap ?? true;
  const step = (delta: number, dir: 'up' | 'down' | 'left' | 'right'): void => {
    // Grids never wrap across rows vertically past the edge: report the edge instead.
    const n = stepIndex(cur, delta, o.count, wrap, o.isDisabled);
    if (n === null) o.onEdge?.(dir, source);
    else setIndex(n);
  };
  const horizontal = o.horizontal ?? false;
  if (a === 'confirm') {
    if (!o.isDisabled?.(cur)) o.onConfirm?.(cur, source);
  } else if (a === 'back') o.onBack?.(source);
  else if (a === 'pause') (o.onPause ?? o.onBack)?.(source);
  else if (horizontal) {
    if (a === 'left') step(-1, a);
    else if (a === 'right') step(1, a);
    else if (o.onAdjust) o.onAdjust(cur, a === 'up' ? -1 : 1, source);
    else o.onEdge?.(a, source);
  } else if (cols > 1) {
    if (a === 'left') {
      if (cur % cols === 0) o.onEdge?.(a, source);
      else step(-1, a);
    } else if (a === 'right') {
      if (cur % cols === cols - 1 || cur === o.count - 1) o.onEdge?.(a, source);
      else step(1, a);
    } else if (a === 'up') {
      if (cur - cols < 0) o.onEdge?.(a, source);
      else step(-cols, a);
    } else if (a === 'down') {
      if (cur + cols >= o.count) o.onEdge?.(a, source);
      else step(cols, a);
    }
  } else if (a === 'up') step(-1, a);
  else if (a === 'down') step(1, a);
  else if (o.onAdjust) o.onAdjust(cur, a === 'left' ? -1 : 1, source);
  else o.onEdge?.(a, source);
};
